import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  MultiTypeDownloadToolTip,
  TableMetadata,
  useTable,
} from "@king-ict/eupisi-ui/components";
import {
  EnrollmentTypeEnum,
  ExportTypeEnum,
  PagedResult,
} from "@king-ict/eupisi-ui/models";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import {
  deleteLocationProgramData,
  getLocationProgramCapacityExportFile,
  searchLocationProgramData,
} from "../../../api";
import {
  editSubsequentCapacityUrl,
  newSubsequentCapacityRoute,
} from "../../../app";
import {
  AdministrationList,
  AdministrationListFilters,
  AdministrationListHeader,
} from "../../../components";
import { usePermission, useBusinessRule } from "../../../hooks";
import {
  LocationProgramDataListFilters,
  LocationProgramDataListItem,
  LocationProgramExportFilterData,
  PermissionEnum,
  WorkOrganizationEnum,
} from "../../../models";
import { SubsequentEnrollmentLocationProgramFilters } from "./SubsequentEnrollmentLocationProgramFilters";

export const SubsequentEnrollmentLocationProgramList = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const [exportType, setExportType] = useState<number>(ExportTypeEnum.Excel);

  const [tableData, setTableData] = useState<
    PagedResult<LocationProgramDataListItem>
  >(({
    rowCount: 0,
    results: [],
  } as unknown) as PagedResult<LocationProgramDataListItem>);

  const filtersRef = useRef<LocationProgramDataListFilters>(
    {} as LocationProgramDataListFilters
  );

  const { hasPermission } = usePermission();
  const {
    canCreateLocationProgramData,
    canDeleteLocationProgramData,
  } = useBusinessRule();

  const tableMetadata = useMemo(
    () =>
      ({
        rowKey: "pedagogicalYearInstitutionProgramDetailId",
        rowToolTip: () => {},
        columns: [
          {
            columnKey: "locationName",
            header: t("locationProgramDetails.location"),
            accessor: "locationName",
          },
          {
            columnKey: "locationProgramName",
            header: t("locationProgramDetails.program"),
            accessor: (locationProgram) => {
              const firstShiftFromTimeSpan = `${locationProgram.firstShiftFrom} - ${locationProgram.firstShiftTo}`;
              const secondShiftTimeSpan =
                locationProgram.workOrganizationId ===
                WorkOrganizationEnum.Shifts
                  ? ` ${t("common.and")} ${locationProgram.secondShiftFrom} - ${
                      locationProgram.secondShiftTo
                    }`
                  : "";

              return `${locationProgram.locationProgramName} (${firstShiftFromTimeSpan}${secondShiftTimeSpan})`;
            },
          },
          {
            columnKey: "locationProgramCapacity",
            header: t("locationProgramDetails.capacity"),
            accessor: "locationProgramCapacity",
          },
          {
            columnKey: "programCapacitySum",
            header: t("locationProgramDetails.programCapacitySum"),
            accessor: "programCapacitySum",
          },
        ],
      } as TableMetadata<LocationProgramDataListItem>),
    [t]
  );

  const [
    tableProps,
    tableState,
    setTableFilters,
  ] = useTable<LocationProgramDataListFilters>({
    metadata: tableMetadata,
    data: tableData.results,
    totalItems: tableData.rowCount,
    pagination: true,
  });

  useEffect(() => {
    (async () => {
      if (tableState.pedagogicalYearId) {
        setTableData(await searchLocationProgramData(tableState));
      }
    })();
  }, [tableState]);

  const handleFiltersChanged = useCallback(
    async (filters: LocationProgramDataListFilters) => {
      const subsequentEnrollmentFilters = {
        ...filters,
        enrollmentTypeId: EnrollmentTypeEnum.SubsequentEnrollment,
      };
      filtersRef.current = subsequentEnrollmentFilters;
      setTableFilters(subsequentEnrollmentFilters);
    },
    [setTableFilters]
  );

  const handleAddNewClicked = () => {
    history.push(newSubsequentCapacityRoute);
  };

  const handleDetailsClicked = (id?: number) => {
    if (id) {
      history.push(editSubsequentCapacityUrl(id!));
    }
  };

  const handleChangeFormat = (typeId: number) => {
    setExportType(typeId);
  };

  const handleDownloadDocument = () => {
    getLocationProgramCapacityExportFile({
      pedagogicalYearId: filtersRef.current.pedagogicalYearId,
      exportTypeId: exportType,
      locationId: filtersRef.current.locationId,
      programId: filtersRef.current.programId,
      enrollmentTypeId: EnrollmentTypeEnum.SubsequentEnrollment,
    } as LocationProgramExportFilterData);
  };

  const handleDeleteConfirmed = async (id: number) => {
    await deleteLocationProgramData(id);

    await handleFiltersChanged({ ...filtersRef.current });

    return true;
  };

  const filterPedagogicalYearId = filtersRef.current.pedagogicalYearId || 0;

  if (!hasPermission(PermissionEnum.LocationProgramDataRead)) {
    return null;
  }

  return (
    <>
      <AdministrationListHeader
        infoMessage={t("locationProgramDetails.listInfoMessage")}
        title={t("locationProgramDetails.subsequentListTitle")}
      />

      <AdministrationListFilters
        isFilteredByYear
        isFilteredByMonth
        callbackOnFiltersChanged
        onFiltersChanged={handleFiltersChanged}
      >
        <SubsequentEnrollmentLocationProgramFilters />
      </AdministrationListFilters>

      <AdministrationList
        tableProps={tableProps}
        isCreateEnabled={canCreateLocationProgramData(
          filterPedagogicalYearId,
          EnrollmentTypeEnum.SubsequentEnrollment,
          filtersRef.current.monthId
        )}
        isDeleteEnabled={canDeleteLocationProgramData(
          filterPedagogicalYearId,
          EnrollmentTypeEnum.SubsequentEnrollment
        )}
        isDetailsEnabled={true}
        deleteActionText={t("locationProgramDetails.deleteActionText")}
        deleteModalTitle={t("locationProgramDetails.deleteModalTitle")}
        deleteModalMessage={t("locationProgramDetails.deleteModalMessage")}
        onAddNewClicked={handleAddNewClicked}
        onDeleteConfirmed={handleDeleteConfirmed}
        onDetailsClicked={handleDetailsClicked}
      />
      <MultiTypeDownloadToolTip
        exportType={exportType}
        onclick={handleDownloadDocument}
        changeFormat={handleChangeFormat}
      />
    </>
  );
};
